<template>
  <div class="tst-page p-4">
    <h2 class="tst-title">Тесты</h2>
    <p class="tst-subtitle">Редактирование вопросов и вариантов ответов по всем тестам системы.
      Удаление — «мягкое» (деактивация): деактивированные вопросы/варианты исчезают из новых
      тестов, но история пройденных попыток остаётся.</p>

    <!-- Селектор теста -->
    <div v-if="testsLoading" class="tst-loading"><b-spinner small class="mr-2" /> Загрузка тестов…</div>
    <div v-else class="tst-tests">
      <button
        v-for="t in tests"
        :key="t.testId"
        class="tst-tab"
        :class="{ active: t.testId === selectedTestId }"
        @click="selectTest(t)"
      >
        {{ testCodeLabel(t.testCode) }}
        <span class="tst-tab-code">{{ t.testCode }}</span>
      </button>
    </div>

    <!-- Сводка по уровням для QUALIFIED -->
    <div v-if="selectedTestCode === 'QUALIFIED'" class="tst-tiers">
      <div
        v-for="lv in ['EASY','MIDDLE','HARD']"
        :key="lv"
        class="tst-tier"
        :class="{ 'tst-tier-warn': activeLevelCount(lv) < qualifiedMin[lv] }"
      >
        {{ lv }}: <strong>{{ activeLevelCount(lv) }}</strong> / нужно {{ qualifiedMin[lv] }}
      </div>
    </div>

    <div v-if="selectedTestId" class="tst-toolbar">
      <b-button size="sm" variant="warning" @click="openAddQuestion">
        <b-icon icon="plus" /> Добавить вопрос
      </b-button>
      <b-form-checkbox v-model="showInactive" switch class="tst-showinactive">Показывать неактивные</b-form-checkbox>
    </div>

    <!-- Вопросы -->
    <div v-if="questionsLoading" class="tst-loading"><b-spinner small class="mr-2" /> Загрузка вопросов…</div>
    <div v-else-if="selectedTestId && !visibleQuestions.length" class="tst-empty">Вопросов нет.</div>

    <div v-else class="tst-qlist">
      <div
        v-for="q in visibleQuestions"
        :key="q.questionId"
        class="tst-qcard"
        :class="{ 'tst-inactive': !q.isActive }"
      >
        <div class="tst-qhead">
          <button class="tst-chev" @click="toggleExpand(q.questionId)">{{ expanded[q.questionId] ? '▾' : '▸' }}</button>
          <div class="tst-qmain">
            <b-form-textarea
              v-model="q.draftText"
              rows="2"
              max-rows="6"
              class="tst-qtext"
              :disabled="!q.isActive"
            />
            <div class="tst-qmeta">
              <b-form-select v-model="q.draftLevel" :options="levelOptions" size="sm" class="tst-level" :disabled="!q.isActive" />
              <span v-if="!q.isActive" class="tst-badge-inactive">Неактивен</span>
              <span class="tst-qid">#{{ q.questionId }}</span>
            </div>
          </div>
          <div class="tst-qactions">
            <b-button
              size="sm" variant="warning"
              :disabled="!questionChanged(q) || q.saving"
              @click="saveQuestion(q)"
            >
              <b-spinner v-if="q.saving" small /> Сохранить
            </b-button>
            <b-button
              size="sm"
              :variant="q.isActive ? 'outline-danger' : 'outline-success'"
              :disabled="q.toggling"
              @click="toggleQuestionActive(q)"
            >
              {{ q.isActive ? 'Деактивировать' : 'Восстановить' }}
            </b-button>
          </div>
        </div>

        <!-- Варианты -->
        <div v-show="expanded[q.questionId]" class="tst-answers">
          <div class="tst-ans-warn" v-if="q.isActive && activeCorrectCount(q) === 0">
            ⚠ Нет ни одного верного активного варианта.
          </div>
          <div
            v-for="a in visibleAnswers(q)"
            :key="a.answerId"
            class="tst-ans"
            :class="{ 'tst-inactive': !a.isActive }"
          >
            <b-form-checkbox v-model="a.draftCorrect" class="tst-ans-correct" :disabled="!a.isActive" title="Верный ответ" />
            <b-form-input v-model="a.draftText" size="sm" class="tst-ans-text" :disabled="!a.isActive" />
            <span v-if="!a.isActive" class="tst-badge-inactive">Неактивен</span>
            <b-button
              size="sm" variant="warning"
              :disabled="!answerChanged(a) || a.saving"
              @click="saveAnswer(q, a)"
            >
              <b-spinner v-if="a.saving" small /> Сохранить
            </b-button>
            <b-button
              size="sm"
              :variant="a.isActive ? 'outline-danger' : 'outline-success'"
              :disabled="a.toggling"
              @click="toggleAnswerActive(q, a)"
            >
              {{ a.isActive ? 'Убрать' : 'Вернуть' }}
            </b-button>
          </div>
          <b-button size="sm" variant="outline-secondary" class="tst-add-ans" @click="openAddAnswer(q)">
            <b-icon icon="plus" /> Добавить вариант
          </b-button>
        </div>
      </div>
    </div>

    <!-- Журнал изменений -->
    <h3 class="tst-log-title">Журнал изменений</h3>
    <b-card class="tst-card" body-class="p-0">
      <div v-if="logLoading" class="tst-loading"><b-spinner small class="mr-2" /> Загрузка…</div>
      <div v-else-if="!logRows.length" class="tst-empty">Изменений пока не было.</div>
      <template v-else>
        <div class="tst-log-row tst-log-row--head">
          <div>Тест</div>
          <div>Объект</div>
          <div>Действие</div>
          <div>Изменение</div>
          <div>Кто</div>
          <div>Когда</div>
        </div>
        <div v-for="e in logRows" :key="e.id" class="tst-log-row">
          <div>{{ e.testCode || '—' }}</div>
          <div>{{ entityLabel(e.entityType) }}<span class="tst-log-id" v-if="e.entityId"> #{{ e.entityId }}</span></div>
          <div><span class="tst-log-action" :class="'act-' + (e.action || '').toLowerCase()">{{ actionLabel(e.action) }}</span></div>
          <div class="tst-log-change">
            <template v-if="e.oldValue != null || e.newValue != null">
              <span v-if="e.oldValue != null" class="tst-log-old">{{ e.oldValue }}</span>
              <span v-if="e.oldValue != null && e.newValue != null" class="tst-log-arr">→</span>
              <span v-if="e.newValue != null" class="tst-log-new">{{ e.newValue }}</span>
            </template>
            <span v-else class="tst-log-summary">{{ e.summary || '—' }}</span>
          </div>
          <div class="tst-log-who">{{ e.changedByName || e.changedByEmail || '—' }}</div>
          <div class="tst-log-when">{{ formatDate(e.changedAt) }}</div>
        </div>
      </template>
    </b-card>

    <!-- Модалка: новый вопрос -->
    <b-modal id="tst-add-question" title="Новый вопрос" ok-title="Добавить" cancel-title="Отмена"
             :ok-disabled="!newQuestion.textRu.trim() || newQuestion.saving" @ok.prevent="createQuestion">
      <b-form-group label="Текст вопроса">
        <b-form-textarea v-model="newQuestion.textRu" rows="3" max-rows="8" />
      </b-form-group>
      <b-form-group label="Уровень / тип" v-if="levelOptions.length">
        <b-form-select v-model="newQuestion.level" :options="levelOptions" />
      </b-form-group>
    </b-modal>

    <!-- Модалка: новый вариант -->
    <b-modal id="tst-add-answer" title="Новый вариант" ok-title="Добавить" cancel-title="Отмена"
             :ok-disabled="!newAnswer.textRu.trim() || newAnswer.saving" @ok.prevent="createAnswer">
      <b-form-group label="Текст варианта">
        <b-form-input v-model="newAnswer.textRu" />
      </b-form-group>
      <b-form-checkbox v-model="newAnswer.isCorrect">Верный ответ</b-form-checkbox>
    </b-modal>
  </div>
</template>

<script>
import { apiDomain, getHeader } from '@/config/config'
import axios from 'axios'

const TEST_LABELS = {
  BASIC: 'Базовый тест',
  QUALIFIED: 'Квалификационный тест',
  PROFESSIONAL: 'Опросник (Professional)',
}
const LEVELS_BY_CODE = {
  QUALIFIED: [
    { value: 'EASY', text: 'EASY' },
    { value: 'MIDDLE', text: 'MIDDLE' },
    { value: 'HARD', text: 'HARD' },
  ],
  PROFESSIONAL: [
    { value: 'SINGLE', text: 'SINGLE (один ответ)' },
    { value: 'MULTI', text: 'MULTI (несколько)' },
  ],
  BASIC: [{ value: '', text: '—' }],
}

export default {
  name: 'TestsEditor',
  data() {
    return {
      tests: [],
      testsLoading: true,
      selectedTestId: null,
      selectedTestCode: '',
      questions: [],
      questionsLoading: false,
      expanded: {},
      showInactive: true,
      logRows: [],
      logLoading: true,
      qualifiedMin: { EASY: 3, MIDDLE: 3, HARD: 4 },
      newQuestion: { textRu: '', level: '', saving: false },
      newAnswer: { questionId: null, textRu: '', isCorrect: false, saving: false },
    }
  },
  computed: {
    levelOptions() {
      return LEVELS_BY_CODE[this.selectedTestCode] || [{ value: '', text: '—' }]
    },
    visibleQuestions() {
      return this.showInactive ? this.questions : this.questions.filter(q => q.isActive)
    },
  },
  created() {
    this.loadTests()
    this.loadLog()
  },
  methods: {
    testCodeLabel(c) { return TEST_LABELS[c] || c },
    entityLabel(t) { return t === 'QUESTION' ? 'Вопрос' : t === 'ANSWER' ? 'Вариант' : t },
    actionLabel(a) {
      return ({ CREATE: 'Создание', UPDATE: 'Изменение', DEACTIVATE: 'Деактивация', RESTORE: 'Восстановление' })[a] || a
    },
    formatDate(iso) {
      if (!iso) return '—'
      const d = new Date(iso)
      if (isNaN(d.getTime())) return iso
      const p = n => String(n).padStart(2, '0')
      return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`
    },
    toast(msg, variant = 'success', title = 'Готово') {
      this.$bvToast && this.$bvToast.toast(msg, {
        title, variant, toaster: 'b-toaster-top-center', solid: true, autoHideDelay: variant === 'danger' ? 4500 : 2500,
      })
    },
    errToast(err, fallback) {
      const sc = err && err.response && err.response.status
      let msg = fallback
      if (sc === 401 || sc === 403) msg = 'Недостаточно прав.'
      else if (sc === 422 && err.response.data) msg = String(err.response.data)
      this.toast(msg, 'danger', 'Ошибка')
    },
    // ── loaders ──
    loadTests() {
      this.testsLoading = true
      axios.get(`${apiDomain}/admin/tests`, { headers: getHeader() })
        .then(res => {
          this.tests = Array.isArray(res.data) ? res.data : []
          if (this.tests.length && !this.selectedTestId) this.selectTest(this.tests[0])
        })
        .catch(err => { this.errToast(err, 'Не удалось загрузить тесты.'); this.tests = [] })
        .finally(() => { this.testsLoading = false })
    },
    selectTest(t) {
      this.selectedTestId = t.testId
      this.selectedTestCode = t.testCode
      this.loadQuestions()
    },
    loadQuestions() {
      if (!this.selectedTestId) return
      this.questionsLoading = true
      axios.get(`${apiDomain}/admin/tests/${this.selectedTestId}/questions`, { headers: getHeader() })
        .then(res => {
          const list = Array.isArray(res.data) ? res.data : []
          this.questions = list.map(q => ({
            questionId: q.questionId,
            isActive: !!q.isActive,
            draftText: q.questionRu || '',
            origText: q.questionRu || '',
            draftLevel: q.level || '',
            origLevel: q.level || '',
            saving: false,
            toggling: false,
            answers: (q.answers || []).map(a => this.mapAnswer(a)),
          }))
        })
        .catch(err => { this.errToast(err, 'Не удалось загрузить вопросы.'); this.questions = [] })
        .finally(() => { this.questionsLoading = false })
    },
    mapAnswer(a) {
      return {
        answerId: a.answerId,
        isActive: !!a.isActive,
        draftText: a.answerRu || '',
        origText: a.answerRu || '',
        draftCorrect: !!a.isCorrect,
        origCorrect: !!a.isCorrect,
        saving: false,
        toggling: false,
      }
    },
    loadLog() {
      this.logLoading = true
      axios.get(`${apiDomain}/admin/test-edit-log`, { headers: getHeader() })
        .then(res => { this.logRows = Array.isArray(res.data) ? res.data : [] })
        .catch(() => { this.logRows = [] })
        .finally(() => { this.logLoading = false })
    },
    // ── derived ──
    visibleAnswers(q) {
      return this.showInactive ? q.answers : q.answers.filter(a => a.isActive)
    },
    activeLevelCount(level) {
      return this.questions.filter(q => q.isActive && (q.origLevel || '').toUpperCase() === level).length
    },
    activeCorrectCount(q) {
      return q.answers.filter(a => a.isActive && a.origCorrect).length
    },
    questionChanged(q) {
      return q.draftText.trim() !== q.origText || q.draftLevel !== q.origLevel
    },
    answerChanged(a) {
      return a.draftText.trim() !== a.origText || a.draftCorrect !== a.origCorrect
    },
    toggleExpand(id) { this.$set(this.expanded, id, !this.expanded[id]) },
    // ── mutations ──
    saveQuestion(q) {
      if (!q.draftText.trim()) { this.toast('Текст вопроса не может быть пустым.', 'danger', 'Ошибка'); return }
      q.saving = true
      axios.post(`${apiDomain}/admin/questions/${q.questionId}`,
        { textRu: q.draftText.trim(), level: q.draftLevel || '' }, { headers: getHeader() })
        .then(() => { this.toast('Вопрос сохранён.'); this.afterMutation() })
        .catch(err => this.errToast(err, 'Не удалось сохранить вопрос.'))
        .finally(() => { q.saving = false })
    },
    toggleQuestionActive(q) {
      q.toggling = true
      axios.post(`${apiDomain}/admin/questions/${q.questionId}/active`,
        { active: !q.isActive }, { headers: getHeader() })
        .then(() => { this.toast(q.isActive ? 'Вопрос деактивирован.' : 'Вопрос восстановлен.'); this.afterMutation() })
        .catch(err => this.errToast(err, 'Не удалось изменить статус вопроса.'))
        .finally(() => { q.toggling = false })
    },
    saveAnswer(q, a) {
      if (!a.draftText.trim()) { this.toast('Текст варианта не может быть пустым.', 'danger', 'Ошибка'); return }
      a.saving = true
      axios.post(`${apiDomain}/admin/answers/${a.answerId}`,
        { textRu: a.draftText.trim(), isCorrect: a.draftCorrect }, { headers: getHeader() })
        .then(() => { this.toast('Вариант сохранён.'); this.afterMutation() })
        .catch(err => this.errToast(err, 'Не удалось сохранить вариант.'))
        .finally(() => { a.saving = false })
    },
    toggleAnswerActive(q, a) {
      a.toggling = true
      axios.post(`${apiDomain}/admin/answers/${a.answerId}/active`,
        { active: !a.isActive }, { headers: getHeader() })
        .then(() => { this.toast(a.isActive ? 'Вариант убран.' : 'Вариант возвращён.'); this.afterMutation() })
        .catch(err => this.errToast(err, 'Не удалось изменить статус варианта.'))
        .finally(() => { a.toggling = false })
    },
    openAddQuestion() {
      this.newQuestion = { textRu: '', level: (this.levelOptions[0] && this.levelOptions[0].value) || '', saving: false }
      this.$bvModal.show('tst-add-question')
    },
    createQuestion() {
      if (!this.newQuestion.textRu.trim()) return
      this.newQuestion.saving = true
      axios.post(`${apiDomain}/admin/tests/${this.selectedTestId}/questions`,
        { textRu: this.newQuestion.textRu.trim(), level: this.newQuestion.level || '' }, { headers: getHeader() })
        .then(() => { this.$bvModal.hide('tst-add-question'); this.toast('Вопрос добавлен.'); this.afterMutation() })
        .catch(err => this.errToast(err, 'Не удалось добавить вопрос.'))
        .finally(() => { this.newQuestion.saving = false })
    },
    openAddAnswer(q) {
      this.newAnswer = { questionId: q.questionId, textRu: '', isCorrect: false, saving: false }
      this.$bvModal.show('tst-add-answer')
    },
    createAnswer() {
      if (!this.newAnswer.textRu.trim()) return
      this.newAnswer.saving = true
      axios.post(`${apiDomain}/admin/questions/${this.newAnswer.questionId}/answers`,
        { textRu: this.newAnswer.textRu.trim(), isCorrect: this.newAnswer.isCorrect }, { headers: getHeader() })
        .then(() => { this.$bvModal.hide('tst-add-answer'); this.toast('Вариант добавлен.'); this.afterMutation() })
        .catch(err => this.errToast(err, 'Не удалось добавить вариант.'))
        .finally(() => { this.newAnswer.saving = false })
    },
    afterMutation() {
      this.loadQuestions()
      this.loadLog()
    },
  },
}
</script>

<style scoped>
.tst-page { max-width: 900px; }
.tst-title { font-size: 22px; font-weight: 800; color: #111827; margin-bottom: 4px; }
.tst-subtitle { color: #6B7280; font-size: 13px; line-height: 1.5; margin-bottom: 18px; max-width: 680px; }
.tst-loading { padding: 20px; color: #6B7280; }
.tst-empty { padding: 18px; color: #9CA3AF; font-size: 13px; }

.tst-tests { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 14px; }
.tst-tab { border: 1px solid #E5E7EB; background: #fff; border-radius: 10px; padding: 8px 14px; font-weight: 600; color: #374151; cursor: pointer; }
.tst-tab.active { border-color: #EFB914; background: #FFF9E8; color: #111827; }
.tst-tab-code { display: block; font-size: 10px; color: #9CA3AF; font-weight: 500; }

.tst-tiers { display: flex; gap: 10px; margin-bottom: 12px; }
.tst-tier { font-size: 12px; color: #374151; background: #F3F4F6; border-radius: 8px; padding: 6px 10px; }
.tst-tier-warn { background: #FEF2F2; color: #B91C1C; }

.tst-toolbar { display: flex; align-items: center; gap: 16px; margin-bottom: 12px; }
.tst-showinactive { font-size: 13px; }

.tst-qlist { display: flex; flex-direction: column; gap: 12px; }
.tst-qcard { border: 1px solid #EAECF0; border-radius: 12px; padding: 12px 14px; background: #fff; }
.tst-qcard.tst-inactive { opacity: 0.7; background: #FAFAFA; }
.tst-qhead { display: flex; gap: 10px; align-items: flex-start; }
.tst-chev { border: none; background: none; font-size: 16px; color: #6B7280; cursor: pointer; padding: 2px 4px; }
.tst-qmain { flex: 1; min-width: 0; }
.tst-qtext { font-size: 14px; }
.tst-qmeta { display: flex; align-items: center; gap: 8px; margin-top: 6px; }
.tst-level { max-width: 200px; }
.tst-qid { font-size: 11px; color: #9CA3AF; }
.tst-badge-inactive { font-size: 11px; color: #B91C1C; background: #FEF2F2; border-radius: 6px; padding: 2px 6px; }
.tst-qactions { display: flex; flex-direction: column; gap: 6px; flex-shrink: 0; }

.tst-answers { margin: 10px 0 0 34px; display: flex; flex-direction: column; gap: 8px; }
.tst-ans-warn { color: #B91C1C; font-size: 12px; }
.tst-ans { display: flex; align-items: center; gap: 8px; }
.tst-ans.tst-inactive { opacity: 0.6; }
.tst-ans-correct { flex-shrink: 0; }
.tst-ans-text { flex: 1; }
.tst-add-ans { align-self: flex-start; }

.tst-log-title { font-size: 15px; font-weight: 700; color: #111827; margin: 26px 0 10px; }
.tst-card { border: 1px solid #EAECF0; border-radius: 12px; overflow: hidden; }
.tst-log-row { display: grid; grid-template-columns: 0.8fr 1fr 1fr 2fr 1.2fr 1fr; align-items: center; gap: 10px; padding: 10px 14px; border-top: 1px solid #F1F3F5; font-size: 12px; }
.tst-log-row:first-child { border-top: none; }
.tst-log-row--head { background: #F9FAFB; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: #6B7280; }
.tst-log-id { color: #9CA3AF; }
.tst-log-action { font-weight: 700; }
.act-create { color: #059669; }
.act-update { color: #2563EB; }
.act-deactivate { color: #B91C1C; }
.act-restore { color: #7C3AED; }
.tst-log-old { color: #9CA3AF; text-decoration: line-through; }
.tst-log-arr { color: #9CA3AF; margin: 0 5px; }
.tst-log-new { color: #111827; font-weight: 600; }
.tst-log-summary { color: #374151; }
.tst-log-who { color: #374151; }
.tst-log-when { color: #6B7280; }
</style>
